import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Heading,
  VStack,
  Text,
  Card,
  Icon,
  Button,
  ButtonGroup,
  DrawerFooter,
} from '@chakra-ui/react';
import { mdiCogs, mdiBookOutline, mdiAccountGroupOutline } from '@mdi/js';
import {
  Media,
  MediaFigure,
  MediaBody,
  MediaTitle,
} from 'features/common/components/MediaObject';
import useModal from 'features/common/hooks/useModal/useModal';
import { tenantsFallbackIcon } from 'features/common/utils/icons';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import { iconByProduct } from 'features/subscriptions/utils';
import { useEffect, useRef, useState } from 'react';
import { useGetDeploymentRegionsQuery } from 'gql/graphql';
import DiscardChangesModal from 'features/common/components/DiscardModal/DiscardChangesModal';
import { StreamTermsModal } from '../term-modals';
import { useCurrentOrgRegion } from 'features/common/components/OrganizationAppAccess/utils';

const sellingPoints: { icon: string; text: string }[] = [
  {
    icon: mdiCogs,
    text: 'Organize and automate workflows',
  },
  {
    icon: mdiAccountGroupOutline,
    text: 'Collaborate and track progress',
  },
  {
    icon: mdiBookOutline,
    text: 'Work more efficiently with agents',
  },
];

export const TryStreamDrawer = () => {
  const ai = iconByProduct.ai;
  const initialFocusRef = useRef(null);
  const { isOpen, setIsOpen, onCloseModal } = useModal();
  const [isTermsOpen, setTermsOpen] = useState(false);
  const [{ tryAiDrawerVisible }, drawerDispatch] = useDrawerStateDispatch();

  const orgRegion = useCurrentOrgRegion();
  const { data } = useGetDeploymentRegionsQuery();
  const aiDeploymentRegions = data?.products?.find(
    (product) => product.code === 'ai',
  );
  const region = aiDeploymentRegions?.deploymentRegions?.nodes?.find(
    ({ id }) => orgRegion === id,
  );

  const ref = useRef<HTMLElement>(document.body);
  useEffect(() => {
    ref.current.style.overflow = 'hidden';
    ref.current.style.touchAction = 'none';
  }, []);

  const clearForm = () => {
    setIsOpen(false);
    drawerDispatch(closeDrawer());
  };

  const onCloseDrawer = () => {
    if (region) return setIsOpen(true);
    setIsOpen(false);
    drawerDispatch(closeDrawer());
  };

  const handleSubmit = () => {
    setTermsOpen(true);
  };

  return (
    <>
      <StreamTermsModal
        isOpen={isTermsOpen}
        onCloseHandler={setTermsOpen}
        region={region?.description}
      />
      <Drawer
        onClose={onCloseDrawer}
        isOpen={tryAiDrawerVisible}
        initialFocusRef={initialFocusRef}
        blockScrollOnMount={false}
        size='lg'
        aria-label={
          tryAiDrawerVisible ? 'trial drawer open' : 'trial drawer closed'
        }
      >
        <DrawerOverlay />
        <DrawerContent aria-label='trial drawer'>
          <HStack pt={7} pb={6}>
            <DrawerCloseButton />
          </HStack>
          <DrawerBody>
            <VStack align={'stretch'} pb={10} gap='8'>
              <VStack align={'start'} gap='8'>
                <Media
                  p={0}
                  as={'div'}
                  size={'2xlarge'}
                  _hover={{
                    backgroundColor: 'transparent',
                    cursor: 'initial',
                  }}
                  _focus={{
                    backgroundColor: 'transparent',
                    cursor: 'initial',
                  }}
                >
                  <MediaFigure
                    icon={{ path: tenantsFallbackIcon }}
                    image={{
                      url: ai.iconUrl,
                      alt: '',
                    }}
                    color={'transparent'}
                  />
                  <MediaBody
                    alignSelf={'center'}
                    display={'flex'}
                    flexDirection={'column'}
                  >
                    <MediaTitle as={'div'}>
                      <HStack>
                        <Heading fontSize='2xl'>{ai.name}</Heading>
                      </HStack>
                    </MediaTitle>
                  </MediaBody>
                </Media>
              </VStack>
              <Card variant='outline' size='md' p='4'>
                <VStack align='flex-start' gap='4'>
                  <HStack align='center'>
                    <Text>
                      Stream Free is available for all customers today. Accept
                      the Sitecore Stream AI terms and conditions to enable it.
                    </Text>
                  </HStack>
                  <VStack align='flex-start' gap='2' mt='2'>
                    <HStack align='flex-start' gap='2'>
                      <Text fontSize='sm' color='gray.600'>
                        •
                      </Text>
                      <Text fontSize='sm' color='gray.600'>
                        <Text as='span' fontWeight='bold'>
                          Ensures Responsible AI Use
                        </Text>
                        : These terms clarify how Sitecore's AI-powered features
                        should be used to ensure ethical, secure, and
                        responsible business practices.
                      </Text>
                    </HStack>
                    <HStack align='flex-start' gap='2'>
                      <Text fontSize='sm' color='gray.600'>
                        •
                      </Text>
                      <Text fontSize='sm' color='gray.600'>
                        <Text as='span' fontWeight='bold'>
                          Protects Your Rights and Data
                        </Text>
                        : They outline how your data is processed and protected
                      </Text>
                    </HStack>
                    <HStack align='flex-start' gap='2'>
                      <Text fontSize='sm' color='gray.600'>
                        •
                      </Text>
                      <Text fontSize='sm' color='gray.600'>
                        <Text as='span' fontWeight='bold'>
                          Sets Clear Expectations
                        </Text>
                        : Accepting the terms ensures mutual understanding of
                        the service scope, limitations, and your permitted
                        usage.
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Card>
              <VStack data-testid='trial-ai-selling-points' align='flex-start'>
                {sellingPoints.map((point, key) => (
                  <HStack
                    width='full'
                    key={key}
                    gap='4'
                    p='2'
                    cursor='pointer'
                    _hover={{ backgroundColor: 'gray.50' }}
                    borderRadius='md'
                    onClick={() =>
                      window.open(
                        'https://www.sitecore.com/products/sitecore-stream/tiers',
                        '_blank',
                      )
                    }
                    role='button'
                  >
                    <Icon
                      layerStyle='icon.subtle'
                      boxSize='icon.lg'
                      color='red'
                    >
                      <path d={point.icon} />
                    </Icon>
                    <Text>{point.text}</Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </DrawerBody>
          <DrawerFooter justifyContent='flex-end'>
            <ButtonGroup>
              <Button
                data-testid='cancel-btn'
                type='button'
                variant='ghost'
                onClick={onCloseDrawer}
                aria-label='cancel trial'
              >
                Cancel
              </Button>
              <Button
                data-testid='submit-btn'
                type='submit'
                variant='solid'
                aria-label='enable trial'
                onClick={handleSubmit}
              >
                Continue
              </Button>
            </ButtonGroup>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <DiscardChangesModal
        isOpen={isOpen}
        onClose={onCloseModal()}
        onDiscard={onCloseModal({ cb: clearForm })}
      />
    </>
  );
};
